/* eslint-disable no-undef */
/**
 * Server-Side In-Memory Cache with LRU Eviction and Request Deduplication
 *
 * Reduces redundant upstream API calls during SSR / server components.
 * Only active server-side (Node.js) — passes through on the client.
 * Feature-flagged via `NEXT_PUBLIC_ENABLE_SERVER_CACHE === "true"`.
 *
 * Eviction strategy: TTL + LRU hybrid.
 *  - Every entry has a TTL (time-to-live). Expired entries are removed every 60s.
 *  - When cache exceeds MAX_ENTRIES, the least-recently-accessed entry is evicted.
 *  - Hot categories stay cached; cold categories naturally get evicted.
 *
 * Memory budget: MAX_ENTRIES × ~20 KB per entry ≈ ~30 MB peak per Node process.
 *
 * Per-pod behavior: each Next.js Node process has its own `Map`. Traffic spread across N pods means
 * each pod warms its own cache; this still cuts origin load for hot keys on each pod but is not a
 * global shared cache (use Redis/CDN for cross-pod aggregation).
 */

const isServer = typeof window === "undefined";

/** @type {Map<string, { data: any, expiry: number, lastAccessed: number }>} */
const cache = new Map();

/** @type {Map<string, Promise<any>>} */
const inFlight = new Map();

const MAX_ENTRIES = 1000;
const CLEANUP_INTERVAL_MS = 60000;

let hits = 0;
let misses = 0;

/**
 * Remove expired entries from cache.
 */
function cleanup() {
  const now = Date.now();
  for (const [key, entry] of cache) {
    if (now >= entry.expiry) {
      cache.delete(key);
    }
  }
}

/**
 * Evict the least-recently-accessed entry when cache exceeds MAX_ENTRIES.
 * LRU ensures hot categories remain cached while cold ones are evicted first.
 */
function evictLRU() {
  if (cache.size <= MAX_ENTRIES) return;

  let lruKey = null;
  let lruTime = Infinity;

  for (const [key, entry] of cache) {
    if (entry.lastAccessed < lruTime) {
      lruTime = entry.lastAccessed;
      lruKey = key;
    }
  }

  if (lruKey) {
    cache.delete(lruKey);
  }
}

if (isServer) {
  const interval = setInterval(cleanup, CLEANUP_INTERVAL_MS);
  if (interval.unref) {
    interval.unref();
  }
}

/**
 * Wraps an async fetch function with server-side in-memory caching and request deduplication.
 *
 * @param {string} cacheKey - Unique key for this cached entry
 * @param {() => Promise<any>} fetchFn - The async function to call on cache miss
 * @param {number} ttlMs - Time-to-live in milliseconds
 * @returns {Promise<any>} - Cached or freshly fetched data
 */
export async function withServerCache(cacheKey, fetchFn, ttlMs) {
  if (!isServer || process.env.NEXT_PUBLIC_ENABLE_SERVER_CACHE !== "true") {
    return fetchFn();
  }

  const now = Date.now();
  const cached = cache.get(cacheKey);
  if (cached && now < cached.expiry) {
    hits++;
    cached.lastAccessed = now;
    return cached.data;
  }

  if (cached) {
    cache.delete(cacheKey);
  }

  if (inFlight.has(cacheKey)) {
    hits++;
    return inFlight.get(cacheKey);
  }

  misses++;
  const promise = fetchFn()
    .then((data) => {
      if (data !== null && data !== undefined) {
        const insertTime = Date.now();
        cache.set(cacheKey, { data, expiry: insertTime + ttlMs, lastAccessed: insertTime });
        evictLRU();
      }
      return data;
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, promise);
  return promise;
}

/**
 * Returns cache statistics for observability/logging.
 */
export function getServerCacheStats() {
  return {
    size: cache.size,
    inFlightCount: inFlight.size,
    hits,
    misses,
    hitRate: hits + misses > 0 ? (hits / (hits + misses)).toFixed(3) : "0.000",
  };
}

/**
 * Clears all cached entries. Useful for testing or emergency production reset.
 */
export function clearServerCache() {
  cache.clear();
  inFlight.clear();
  hits = 0;
  misses = 0;
}
